import { useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import PawLinkQR from "@/components/PawLinkQR";
import {
    DeviceMobile, ShieldCheck, DownloadSimple, Lightning, ArrowsClockwise,
    AppleLogo, GoogleLogo, X, CheckCircle, Syringe,
    Warning, User, ClockCounterClockwise, Phone, WhatsappLogo, Gift,
    FirstAid,
} from "@phosphor-icons/react";
import ShareMenu from "@/components/ShareMenu";

const easeOut = [0.16, 1, 0.3, 1];

// Unique premium Toy Poodle photo used ONLY inside the Install phone mockup.
// Not reused anywhere else on the site.
const POODLE_IMG = "https://images.unsplash.com/photo-1549297161-14f79605a74c?auto=format&fit=crop&w=800&q=85";

/**
 * InstallSection — website-only "Install PawLink" premium section.
 * Adaptive to device:
 *   - Android / Chrome / Edge desktop → captures `beforeinstallprompt`,
 *     shows big "Install PawLink" button that triggers the native install prompt.
 *   - iOS Safari → opens a step-by-step instruction modal ("Add to Home Screen").
 *   - Desktop (no capture available) → shows a big QR code linking to the install URL.
 *
 * MUST only be rendered on the marketing website. Self-hides in standalone (installed) mode.
 */
export default function InstallSection({ t }) {
    const reduce = useReducedMotion();
    const [device, setDevice] = useState("desktop"); // 'android' | 'ios' | 'desktop'
    const [installPromptEvent, setInstallPromptEvent] = useState(null);
    const [installState, setInstallState] = useState("idle"); // idle | installing | installed
    const [showIosModal, setShowIosModal] = useState(false);
    // Universal fallback modal — shown whenever we can't fire a native install
    // prompt (Firefox, Safari macOS, older Chrome, or Chrome/Edge that hasn't
    // fired `beforeinstallprompt` yet). Contains per-browser instructions so
    // clicking "Install" is NEVER a dead click.
    const [showHowToModal, setShowHowToModal] = useState(false);
    const promptRef = useRef(null);

    // Device detection
    useEffect(() => {
        const ua = navigator.userAgent || "";
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isAndroid = /Android/i.test(ua);
        if (isIOS) setDevice("ios");
        else if (isAndroid) setDevice("android");
        else setDevice("desktop");
    }, []);

    // Capture beforeinstallprompt (Android + Desktop Chrome/Edge)
    useEffect(() => {
        const onPrompt = (e) => {
            e.preventDefault();
            promptRef.current = e;
            setInstallPromptEvent(e);
        };
        const onInstalled = () => {
            setInstallState("installed");
            promptRef.current = null;
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    // Standalone detection — hide entire section inside the installed PWA / TWA.
    const isStandalone = useMemo(() => {
        try {
            return (
                window.matchMedia("(display-mode: standalone)").matches ||
                window.navigator.standalone === true
            );
        } catch { return false; }
    }, []);

    const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;

    const handleInstallClick = async () => {
        // iOS Safari has no programmatic prompt — show the illustrated
        // Share → Add to Home Screen walkthrough.
        if (device === "ios") { setShowIosModal(true); return; }

        // PawLink is a PWA. On every browser that supports installability
        // (Android Chrome / Edge / Samsung Internet, Desktop Chrome / Edge /
        // Brave / Opera) the browser fires `beforeinstallprompt` and we
        // trigger the native one-tap install prompt right here. NEVER
        // redirect to a store, NEVER show a "coming soon" modal.
        if (promptRef.current) {
            setInstallState("installing");
            try {
                await promptRef.current.prompt();
                const choice = await promptRef.current.userChoice;
                setInstallState(choice?.outcome === "accepted" ? "installed" : "idle");
                promptRef.current = null; setInstallPromptEvent(null);
                return;
            } catch { setInstallState("idle"); }
        }

        // Browser didn't expose the prompt (Firefox, Safari on macOS, older
        // Chrome that hasn't hit its engagement heuristic yet, or a user
        // that already dismissed the prompt in this browser). Show the
        // universal browser-specific walkthrough so the click never dead-ends.
        setShowHowToModal(true);
    };

    const installUrl = useMemo(() => `${SITE_URL}/install?src=qr`, []);

    // Hide entirely when running as an installed app.
    if (isStandalone) return null;

    let ctaLabel = t("install.cta");
    let CTAIcon = DownloadSimple;
    if (installState === "installing") { ctaLabel = t("install.cta_installing"); }
    else if (installState === "installed") { ctaLabel = t("install.cta_open"); CTAIcon = CheckCircle; }
    else if (device === "ios") { CTAIcon = AppleLogo; }
    else if (device === "android") { CTAIcon = GoogleLogo; }

    const badges = [
        { Icon: ShieldCheck, label: t("install.badge_secure") },
        { Icon: DownloadSimple, label: t("install.badge_free") },
        { Icon: Lightning, label: t("install.badge_fast") },
        { Icon: ArrowsClockwise, label: t("install.badge_updates") },
    ];

    return (
        <section
            data-testid="install-section"
            className="relative isolate overflow-hidden py-14 sm:py-24 lg:py-32 bg-gradient-to-b from-paw-night via-[hsl(152,15%,6%)] to-paw-night text-white"
        >
            {/* Ambient glows */}
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute top-1/3 -start-24 w-[420px] h-[420px] rounded-full blur-[140px] bg-paw-terra/20" />
                <div className="absolute -bottom-40 end-1/4 w-[540px] h-[540px] rounded-full blur-[160px] bg-paw-forest/25" />
            </div>

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid gap-10 lg:gap-20 lg:grid-cols-2 items-center">
                    {/* Left — copy + install + share */}
                    <div className="max-w-xl">
                        <motion.p
                            initial={{ opacity: 0, y: reduce ? 0 : 14 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.6, ease: easeOut }}
                            className="inline-flex items-center gap-2 rounded-full bg-white/8 backdrop-blur border border-white/10 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-terra"
                            dir="ltr"
                        >
                            <DeviceMobile size={12} weight="fill" />
                            Install PawLink
                        </motion.p>

                        <motion.h2
                            initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.8, ease: easeOut, delay: 0.1 }}
                            className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
                        >
                            {t("install.title")}
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: reduce ? 0 : 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.8, ease: easeOut, delay: 0.18 }}
                            className="mt-5 text-base sm:text-lg leading-relaxed text-white/70"
                        >
                            {t("install.subtitle")}
                        </motion.p>

                        {/* Primary CTAs — Install + Share */}
                        <motion.div
                            initial={{ opacity: 0, y: reduce ? 0 : 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.8, ease: easeOut, delay: 0.26 }}
                            className="mt-8 flex flex-wrap items-center gap-3"
                        >
                            <button
                                type="button"
                                data-testid="install-cta"
                                onClick={handleInstallClick}
                                disabled={installState === "installing"}
                                className="group inline-flex items-center gap-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-70 disabled:cursor-wait px-7 py-4 text-white font-semibold shadow-[0_10px_40px_-10px_rgba(16,185,129,0.6)] transition-all duration-300 hover:-translate-y-0.5"
                            >
                                <CTAIcon size={18} weight="fill" />
                                {ctaLabel}
                            </button>

                            {/* Share PawLink — never blocks the primary flow. Distinct outline style. */}
                            <ShareMenu
                                t={t}
                                url={SITE_URL}
                                title="PawLink"
                                text={t("install.share_message")}
                            />
                        </motion.div>

                        {device === "desktop" && !installPromptEvent && !isStandalone && (
                            <p className="mt-3 text-xs text-white/50" data-testid="install-hint-desktop">
                                {t("install.already_installed_hint")}
                            </p>
                        )}

                        {/* Trust badges */}
                        <motion.ul
                            initial={{ opacity: 0, y: reduce ? 0 : 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-15%" }}
                            transition={{ duration: 0.8, ease: easeOut, delay: 0.36 }}
                            className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3"
                            data-testid="install-badges"
                        >
                            {badges.map((b) => (
                                <li key={b.label} className="flex items-center gap-2 text-sm text-white/75">
                                    <b.Icon size={16} weight="fill" className="text-emerald-400 flex-shrink-0" />
                                    <span>{b.label}</span>
                                </li>
                            ))}
                        </motion.ul>
                    </div>

                    {/* Right — Phone mockup + optional desktop QR */}
                    <div className="relative flex justify-center lg:justify-end">
                        <PhoneMockup t={t} />

                        {device === "desktop" && (
                            <motion.div
                                id="install-qr"
                                initial={{ opacity: 0, y: reduce ? 0 : 24, scale: reduce ? 1 : 0.96 }}
                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                viewport={{ once: true, margin: "-10%" }}
                                transition={{ duration: 0.9, ease: easeOut, delay: 0.32 }}
                                data-testid="install-qr-card"
                                className="hidden lg:block absolute -start-4 xl:-start-16 bottom-4 rounded-3xl bg-white p-5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] border border-black/5"
                            >
                                <div className="w-[172px] h-[172px] rounded-2xl bg-white grid place-items-center">
                                    <PawLinkQR value={installUrl} size={156} />
                                </div>
                                <p className="mt-3 max-w-[172px] text-[11px] leading-snug font-medium text-paw-ink text-center">
                                    {t("install.desktop_qr_title")}
                                </p>
                            </motion.div>
                        )}
                    </div>
                </div>
            </div>

            {showIosModal && <IosInstallModal t={t} onClose={() => setShowIosModal(false)} />}
            {showHowToModal && (
                <HowToInstallModal
                    t={t}
                    onClose={() => setShowHowToModal(false)}
                    installUrl={installUrl}
                />
            )}
        </section>
    );
}

/* =====================================================================
 * PhoneMockup — Premium public RESCUE PROFILE shown after a stranger
 * scans a PawLink QR tag. Marketing asset (not a live profile) — designed
 * to convey "trust, premium, easy to help" in under 3 seconds.
 * ===================================================================== */
function PhoneMockup({ t }) {
    return (
        <div
            data-testid="install-phone-mockup"
            className="relative w-[260px] sm:w-[320px] lg:w-[360px] aspect-[9/19] rounded-[46px] bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] p-3 shadow-[0_50px_120px_-30px_rgba(248,95,30,0.35),0_20px_60px_-10px_rgba(0,0,0,0.6)] ring-1 ring-white/10"
        >
            {/* Notch */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 w-28 h-6 rounded-full bg-black z-10 flex items-center justify-center">
                <span className="w-2 h-2 rounded-full bg-white/15" />
            </div>

            {/* Screen */}
            <div className="relative w-full h-full rounded-[36px] overflow-hidden bg-[#0B0D10] flex flex-col">
                {/* Status bar */}
                <div className="flex items-center justify-between px-6 pt-3.5 pb-1 text-[10px] text-white/80 font-medium" dir="ltr">
                    <span>9:41</span>
                    <div className="flex items-center gap-1">
                        <span className="w-3 h-2 rounded-sm bg-white/70" />
                        <span className="w-1 h-2 rounded-sm bg-white/70" />
                        <span className="w-5 h-2.5 rounded-sm border border-white/70" />
                    </div>
                </div>

                {/* QR scanned success banner */}
                <div className="mx-3 mt-1.5 rounded-xl bg-gradient-to-r from-emerald-500/20 to-emerald-500/10 border border-emerald-400/30 px-2.5 py-1.5 flex items-center gap-2" dir="ltr">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 grid place-items-center flex-shrink-0">
                        <CheckCircle size={11} weight="fill" className="text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[9.5px] font-bold text-emerald-300 leading-tight">{t("install.petcard_scan_title")}</p>
                        <p className="text-[8px] text-white/55 leading-tight truncate">{t("install.petcard_scan_sub")}</p>
                    </div>
                    <span className="font-mono text-[8px] text-white/40" dir="ltr">PAW·4F1A</span>
                </div>

                {/* Card body */}
                <div className="flex-1 flex flex-col px-3 pt-2 pb-3 gap-1.5 overflow-hidden">
                    {/* Hero photo card with name/breed overlay */}
                    <div className="relative rounded-2xl overflow-hidden aspect-[4/3] flex-shrink-0 ring-1 ring-white/10">
                        <img
                            src={POODLE_IMG}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/25 to-transparent" />

                        {/* PawLink verified chip */}
                        <div className="absolute top-2 end-2 inline-flex items-center gap-1 rounded-full bg-paw-terra/95 text-white text-[7.5px] font-bold uppercase tracking-wider px-2 py-0.5 shadow-md backdrop-blur" dir="ltr">
                            <ShieldCheck size={9} weight="fill" />
                            {t("install.petcard_verified")}
                        </div>

                        {/* Name + breed/gender/age line */}
                        <div className="absolute bottom-2 start-2.5 end-2.5 text-white">
                            <p className="font-display text-[19px] leading-none tracking-tight">{t("install.petcard_pet_name")}</p>
                            <p className="text-[10px] opacity-95 mt-1 leading-tight">
                                {t("install.petcard_pet_breed")} · {t("install.petcard_age_value")}
                            </p>
                        </div>
                    </div>

                    {/* Reward pill — spotlight row */}
                    <div className="rounded-xl bg-gradient-to-r from-paw-terra/25 to-amber-400/10 border border-paw-terra/40 px-2.5 py-1.5 flex items-center gap-2">
                        <div className="w-6 h-6 rounded-md bg-paw-terra grid place-items-center flex-shrink-0 shadow">
                            <Gift size={12} weight="fill" className="text-white" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-[7.5px] uppercase tracking-wider text-white/55 font-semibold leading-none">{t("install.petcard_reward")}</p>
                            <p className="text-[11px] text-white font-semibold leading-tight mt-0.5">{t("install.petcard_reward_value")}</p>
                        </div>
                    </div>

                    {/* Vaccinations */}
                    <PetRow
                        Icon={Syringe}
                        tint="emerald"
                        label={t("install.petcard_vaccinations")}
                        value={t("install.petcard_up_to_date")}
                        badge="✓"
                    />

                    {/* Medical / allergies */}
                    <PetRow
                        Icon={FirstAid}
                        tint="rose"
                        label={t("install.petcard_allergies")}
                        value={t("install.petcard_allergy_value")}
                    />

                    {/* Owner + emergency notes */}
                    <PetRow
                        Icon={User}
                        tint="violet"
                        label={t("install.petcard_owner")}
                        value={t("install.petcard_owner_value")}
                    />
                    <PetRow
                        Icon={Warning}
                        tint="amber"
                        label={t("install.petcard_notes")}
                        value={t("install.petcard_notes_value")}
                    />

                    {/* Action buttons — Call + WhatsApp */}
                    <div className="mt-auto grid grid-cols-2 gap-1.5" dir="ltr">
                        <div className="rounded-xl bg-emerald-500 hover:bg-emerald-400 transition text-white px-2 py-2 flex items-center justify-center gap-1.5 shadow-[0_6px_20px_-8px_rgba(16,185,129,0.7)]">
                            <Phone size={11} weight="fill" />
                            <span className="text-[10px] font-semibold leading-none">{t("install.petcard_call")}</span>
                        </div>
                        <div className="rounded-xl bg-[#25D366] hover:bg-[#20BD5A] transition text-white px-2 py-2 flex items-center justify-center gap-1.5 shadow-[0_6px_20px_-8px_rgba(37,211,102,0.7)]">
                            <WhatsappLogo size={11} weight="fill" />
                            <span className="text-[10px] font-semibold leading-none">{t("install.petcard_whatsapp")}</span>
                        </div>
                    </div>

                    {/* Updated timestamp */}
                    <div className="flex items-center gap-1.5 text-[9px] text-white/40 justify-center" dir="ltr">
                        <ClockCounterClockwise size={9} weight="fill" />
                        <span>{t("install.petcard_updated")} · {t("install.petcard_updated_time")}</span>
                    </div>
                </div>

                {/* Home indicator */}
                <div className="pb-2 flex justify-center">
                    <span className="w-24 h-1 rounded-full bg-white/40" />
                </div>
            </div>
        </div>
    );
}

function PetRow({ Icon, tint, label, value, badge }) {
    const tintMap = {
        emerald: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/25",
        rose:    "bg-rose-500/15 text-rose-300 ring-rose-500/25",
        amber:   "bg-amber-500/15 text-amber-300 ring-amber-500/25",
        violet:  "bg-violet-500/15 text-violet-300 ring-violet-500/25",
    };
    return (
        <div className="rounded-lg bg-white/[0.05] border border-white/10 px-2.5 py-1.5 flex items-center gap-2">
            <div className={`w-6 h-6 rounded-md ring-1 grid place-items-center flex-shrink-0 ${tintMap[tint] || tintMap.emerald}`}>
                <Icon size={11} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <p className="text-[7.5px] uppercase tracking-wider text-white/50 font-semibold leading-none">{label}</p>
                <p className="text-[10px] text-white/95 leading-tight mt-0.5 truncate">{value}</p>
            </div>
            {badge && (
                <span className="text-[10px] font-bold text-emerald-300 flex-shrink-0">{badge}</span>
            )}
        </div>
    );
}

function IosInstallModal({ t, onClose }) {
    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    return (
        <div
            data-testid="install-ios-modal"
            className="fixed inset-0 z-[60] grid place-items-center px-4"
            role="dialog"
            aria-modal="true"
        >
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-sm rounded-3xl bg-white dark:bg-[hsl(152,15%,10%)] p-6 sm:p-7 shadow-2xl">
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="install-ios-modal-close"
                    className="absolute top-3 end-3 w-9 h-9 rounded-full bg-paw-muted dark:bg-white/10 grid place-items-center text-paw-ink dark:text-white hover:opacity-80 transition"
                    aria-label="Close"
                >
                    <X size={16} weight="bold" />
                </button>

                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-black to-[#1a1a1a] text-white grid place-items-center shadow-lg">
                    <AppleLogo size={28} weight="fill" />
                </div>
                <p className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-paw-terra/12 border border-paw-terra/25 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold text-paw-terra">
                    {t("install.ios_coming_kicker")}
                </p>
                <h3 className="mt-3 font-display text-3xl text-paw-ink dark:text-white leading-tight tracking-tight">
                    {t("install.ios_coming_title")}
                </h3>
                <p className="mt-3 text-sm sm:text-base text-paw-ink2 dark:text-white/70 leading-relaxed">
                    {t("install.ios_coming_sub")}
                </p>

                <div className="mt-5 rounded-2xl border border-paw-line dark:border-white/10 bg-paw-muted dark:bg-white/[0.03] p-4">
                    <p className="text-[11px] uppercase tracking-widest font-bold text-paw-ink2 dark:text-white/50">{t("install.ios_meanwhile_label")}</p>
                    <p className="mt-1 text-sm text-paw-ink dark:text-white leading-relaxed">{t("install.ios_meanwhile_body")}</p>
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    data-testid="install-ios-modal-confirm"
                    className="mt-6 w-full rounded-full bg-paw-terra hover:bg-paw-terraDark py-3 text-sm font-semibold text-white transition"
                >
                    {t("install.ios_modal_close")}
                </button>
            </div>
        </div>
    );
}

/* =====================================================================
 * HowToInstallModal — Universal fallback shown when we can't fire the
 * native beforeinstallprompt (Firefox, Safari on macOS, older Chrome,
 * or Chrome/Edge with the prompt on cooldown). Detects the current
 * browser and shows the exact step-by-step install path so the click
 * is never a dead-end.
 *
 * Also renders a QR code as a universal fallback — users can scan it
 * from a phone to install the PWA directly.
 * ===================================================================== */
function detectBrowser() {
    if (typeof navigator === "undefined") return "other";
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (isIOS) return "ios_safari";
    // Order matters — Edge and Brave both ship "Chrome" in UA.
    if (/\bEdg\//.test(ua)) return "edge";
    if (/\bBrave\//.test(ua) || (navigator.brave && navigator.brave.isBrave)) return "brave";
    if (/\bOPR\//.test(ua) || /\bOpera\//.test(ua)) return "opera";
    if (/Firefox\//.test(ua)) return "firefox";
    if (/Chrome\//.test(ua) && /Google/.test(navigator.vendor || "")) return "chrome";
    if (/Safari\//.test(ua) && /Apple/.test(navigator.vendor || "")) return "safari";
    return "other";
}

function HowToInstallModal({ t, onClose, installUrl }) {
    const browser = useMemo(() => detectBrowser(), []);
    // Copy-to-clipboard state for the install URL.
    const [copied, setCopied] = useState(false);
    const copyUrl = async () => {
        try {
            await navigator.clipboard.writeText(installUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch { /* clipboard blocked; noop */ }
    };

    // Per-browser step lists. All strings live in the browser locally to keep
    // this fully self-contained (no new i18n keys required for shipping).
    const steps = {
        chrome:  ["Open the menu (⋮) in the top-right corner.", "Click \"Install PawLink…\" or \"Cast, save & share → Install page as app…\".", "Confirm by clicking \"Install\". PawLink will open in its own window."],
        edge:    ["Open the menu (…) in the top-right corner.", "Click \"Apps\" → \"Install this site as an app\".", "Name it \"PawLink\" and click \"Install\"."],
        brave:   ["Click the menu (☰) in the top-right corner.", "Choose \"Install PawLink\" from the menu.", "Confirm the install prompt."],
        opera:   ["Click the Opera menu → \"Install PawLink…\".", "Confirm to add PawLink to your desktop."],
        firefox: ["Firefox on desktop doesn't support installing web apps.", "To install PawLink, either scan the QR code below with your phone, or open getpawlink.com in Chrome, Edge, or Brave."],
        safari:  ["Safari on macOS doesn't support installing web apps as standalone.", "To install PawLink, either scan the QR code below with your iPhone/iPad, or open getpawlink.com in Chrome, Edge, or Brave."],
        ios_safari: ["Tap the Share button (the square with an arrow) at the bottom.", "Scroll and tap \"Add to Home Screen\".", "Tap \"Add\" in the top-right — PawLink now lives on your Home Screen."],
        other:   ["Open the browser menu.", "Look for \"Install app\" / \"Add to Home Screen\" / \"Install this site as an app\".", "Or scan the QR code below to install from your phone."],
    }[browser];

    const browserLabel = {
        chrome: "Chrome", edge: "Microsoft Edge", brave: "Brave", opera: "Opera",
        firefox: "Firefox", safari: "Safari (macOS)", ios_safari: "Safari (iOS)", other: "your browser",
    }[browser];

    return (
        <div
            data-testid="install-howto-modal"
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
            onClick={onClose}
            role="dialog" aria-modal="true"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="w-full sm:max-w-lg bg-white dark:bg-[hsl(152,15%,10%)] rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 relative max-h-[92vh] overflow-y-auto ring-1 ring-black/5 dark:ring-white/10"
            >
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="install-howto-modal-close"
                    aria-label="Close"
                    className="absolute top-4 end-4 w-9 h-9 rounded-full bg-black/5 dark:bg-white/10 hover:bg-black/10 dark:hover:bg-white/15 flex items-center justify-center text-paw-ink dark:text-white transition"
                >
                    <X size={16} weight="bold" />
                </button>

                <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-2xl bg-emerald-500/15 dark:bg-emerald-500/20 ring-1 ring-emerald-500/25 flex items-center justify-center">
                        <DownloadSimple size={22} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                        <h3 className="font-display text-2xl font-medium tracking-tight text-paw-ink dark:text-white">
                            Install PawLink on {browserLabel}
                        </h3>
                        <p className="text-xs text-paw-ink2 dark:text-white/60 mt-0.5">Takes about 15 seconds.</p>
                    </div>
                </div>

                <ol className="mt-4 space-y-3 text-[15px] leading-relaxed text-paw-ink dark:text-white/90" data-testid="install-howto-steps">
                    {steps.map((s, i) => (
                        <li key={i} className="flex gap-3">
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                            <span className="pt-0.5">{s}</span>
                        </li>
                    ))}
                </ol>

                {/* Universal fallback: QR code — always works from any phone. */}
                <div className="mt-6 pt-6 border-t border-paw-line dark:border-white/10 flex items-center gap-4" data-testid="install-howto-qr">
                    <div className="flex-shrink-0 rounded-xl bg-white p-2 shadow-sm ring-1 ring-black/5">
                        <PawLinkQR value={installUrl} size={92} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-paw-ink dark:text-white">Or install from your phone</p>
                        <p className="text-xs text-paw-ink2 dark:text-white/60 mt-1 leading-snug">
                            Scan this QR code with your phone camera to open PawLink and install it directly on your Home Screen.
                        </p>
                        <button
                            type="button"
                            onClick={copyUrl}
                            data-testid="install-howto-copy-url"
                            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                            {copied ? <><CheckCircle size={12} weight="fill" /> Copied!</> : <>Copy install link</>}
                        </button>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    data-testid="install-howto-modal-dismiss"
                    className="mt-6 w-full rounded-full border border-paw-line dark:border-white/15 bg-transparent hover:bg-paw-muted dark:hover:bg-white/5 py-3 text-sm font-semibold text-paw-ink dark:text-white transition"
                >
                    Got it
                </button>
            </div>
        </div>
    );
}

